import type { ReactElement } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { abbrNumberFilter } from "app/filters/ngFilters";
import TagsBadge from "app/pages/sales-intelligence/common-components/TagsBadge/TagsBadge";
import { EMPTY_ARRAY } from "app/pages/sales-intelligence/constants/common";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import type { CompanyCard } from "app/pages/sales-intelligence/modules/company-analysis/types/company";
import { useIsIndustryTaxonomyOptedIn } from "app/pages/sales-intelligence/modules/industry/hooks/useIsIndustryTaxonomyOptedIn";
import { urlFilterValues } from "app/pages/sales-intelligence/modules/leads-search-filters";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { replaceUnderscore } from "app/pages/sales-intelligence/utils/strings";
import { formatTaxonomyPathDisplayNames } from "app/pages/sales-intelligence/utils/taxonomy-tree/path";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { SiteTagsContainer } from "../../../../containers/SiteTagsContainer/SiteTagsContainer";
import CompanyCardAssets from "../CompanyCardAssets/CompanyCardAssets";
import CompanyCardHeader from "../CompanyCardHeader/CompanyCardHeader";
import Description from "../Description/Description";

import { StyledContentDivider } from "./styles";

type Props = {
    data: CompanyCard;
};

/**
 * Tooltips its child only when there is something to say. The taxonomy has no peer count until the backend
 * supplies one, and an empty tooltip reads as "0 similar companies" rather than "unknown".
 * The `div` is the anchor `Tooltip` needs; without a tooltip the child stands alone.
 */
const MaybeTooltip = ({ content, children }: { content?: string; children: ReactElement }) =>
    content ? (
        <Tooltip content={content}>
            <div>{children}</div>
        </Tooltip>
    ) : (
        children
    );

const CompanyCardContent = ({ data }: Props) => {
    const { translate } = useTranslate();

    const isIndustryTaxonomyOptedIn = useIsIndustryTaxonomyOptedIn();

    // Which classification the badge speaks (ADR-0013). Falls back to the legacy industry while the backend
    // has yet to serve the taxonomy field, so an opted-in account keeps a badge rather than losing one.
    const industryTaxonomy = isIndustryTaxonomyOptedIn ? data.industryTaxonomy : undefined;

    /**
     * Both classifications render the same badge; only the label, the peer count and the seeded filter
     * differ. `domainsCount` decides the tooltip: the legacy path keeps its historic `?? 0` (always
     * tooltipped), while an absent taxonomy count omits the tooltip rather than claiming zero peers.
     *
     * `filters` is what the deep link carries. The taxonomy is addressed by its opaque node id, never by the
     * display path (ADR-0013); `urlFilterValues` owns the value shapes so this card needs neither.
     */
    const industryBadge = industryTaxonomy
        ? {
              text: formatTaxonomyPathDisplayNames(industryTaxonomy.path),
              domainsCount: industryTaxonomy.domainsCount,
              filters: urlFilterValues.industryTaxonomyNode(industryTaxonomy.id),
          }
        : data.industry
          ? {
                text: replaceUnderscore(data.industry),
                domainsCount: data.industryDomainsCount ?? 0,
                filters: urlFilterValues.legacyIndustry(replaceUnderscore(data.industry)),
            }
          : undefined;

    const handleIndustryTagClick = () => {
        if (!industryBadge) {
            return;
        }

        companyAnalysisTrackingService.overview.trackIndustryClick(industryBadge.text);

        void siNavigator.goToAdvancedSearchPage({}, { filtersPayload: industryBadge.filters });
    };

    return (
        <Stack
            justifyContent="flex-start"
            flexGrow="1"
            padding="24px"
            gap="16px"
            minHeight="208px"
        >
            <CompanyCardHeader company={data} />
            <Flex
                alignItems="flex-start"
                gap="72px"
            >
                <Box flex="1 1 0">
                    <Description description={data.description} />
                </Box>

                <Box flex="none">
                    <CompanyCardAssets
                        websites={data.digitalAssets.websites}
                        apps={data.digitalAssets.apps}
                    />
                </Box>
            </Flex>

            <StyledContentDivider />

            <Flex
                alignItems="flex-start"
                gap="68px"
                flexGrow="1"
            >
                {industryBadge ? (
                    <Box flex="none">
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.carbon[250]}
                            margin="4px 0"
                            display="block"
                        >
                            {`${translate("common.industry")}:`}
                        </Text>
                        <MaybeTooltip
                            content={
                                is.undefined(industryBadge.domainsCount)
                                    ? undefined
                                    : translate("si.company-analysis.site-tag.similar-companies", {
                                          count: abbrNumberFilter()(industryBadge.domainsCount),
                                      })
                            }
                        >
                            <TagsBadge
                                dataAutomationType="company-category"
                                text={industryBadge.text}
                                icon={
                                    <SWReactIcons
                                        size="xs"
                                        iconName="market"
                                        iconColor={colorsPalettes.midnight[400]}
                                    />
                                }
                                onClick={handleIndustryTagClick}
                            />
                        </MaybeTooltip>
                    </Box>
                ) : null}

                {data.tags?.length ? <SiteTagsContainer siteTags={data.tags ?? EMPTY_ARRAY} /> : null}
            </Flex>
        </Stack>
    );
};

export default CompanyCardContent;
